import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Check, Loader2, ShieldAlert } from 'lucide-react'
import { OAuthErrorCard } from '../OAuthErrorCard'
import { runOAuthStep, scopeLines, sessionOrSignIn, signedOAuthQuery } from '../oauthFlow'
import type { OAuthFlowError } from '../oauthFlow'
import { getOAuthPublicClient, submitOAuthConsent } from '@/lib/auth/authApi'
import { getAuthUser } from '@/lib/auth/authSession'
import { Button } from '@/components/ui/button'
import { AUTH_BUTTON_CLASS, AuthHeader } from '@/routes/features/authentication/components/AuthFormParts'

export const Route = createFileRoute('/_AuthLayout/oauth/consent')({
  component: OAuthConsentPage,
})

const hostOf = (uri: string | null): string | null => {
  try {
    return uri ? new URL(uri).host : null
  } catch {
    return null
  }
}

/**
 * The MCP OAuth provider's consent page. Clients register themselves (dynamic client
 * registration), so their name is unverified: the redirect host is shown alongside it.
 */
function OAuthConsentPage() {
  const { t } = useTranslation('auth')
  const query = useMemo(() => new URLSearchParams(signedOAuthQuery()), [])
  const [session] = useState(() => sessionOrSignIn())
  const [clientName, setClientName] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState<'allow' | 'deny' | null>(null)
  const [error, setError] = useState<OAuthFlowError | null>(null)

  const clientId = query.get('client_id')
  const redirectHost = hostOf(query.get('redirect_uri'))
  const scopes = scopeLines(query.get('scope'))
  const email = getAuthUser()?.email ?? null

  useEffect(() => {
    if (!session || !clientId) return
    getOAuthPublicClient(session, clientId)
      .then((client) => setClientName(client.name))
      .catch(() => setClientName(null))
  }, [session, clientId])

  if (!session) return null
  if (!clientId || error) return <OAuthErrorCard error={error ?? 'invalidRequest'} />

  const decide = async (accept: boolean) => {
    setSubmitting(accept ? 'allow' : 'deny')
    const result = await runOAuthStep(() => submitOAuthConsent(session, signedOAuthQuery(), accept))
    if (!result) return
    if ('url' in result) {
      window.location.href = result.url
      return
    }
    setError(result.error)
    setSubmitting(null)
  }

  return (
    <div className="space-y-6">
      <AuthHeader
        title={t('oauth.consentTitle', { client: clientName ?? t('oauth.unknownClient') })}
        subtitle={t('oauth.consentSubtitle')}
      />

      {scopes.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[15px] font-medium">{t('oauth.scopesHeading')}</p>
          <ul className="space-y-2">
            {scopes.map((key) => (
              <li key={key} className="flex items-start gap-2 text-[15px]">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                <span>{t(`oauth.scopes.${key}`)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-[14px] text-muted-foreground">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="space-y-1">
          <p>{t('oauth.unverified')}</p>
          {redirectHost ? <p>{t('oauth.redirectsTo', { host: redirectHost })}</p> : null}
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row-reverse">
        <Button className={`${AUTH_BUTTON_CLASS} sm:flex-1`} disabled={submitting !== null} onClick={() => void decide(true)}>
          {submitting === 'allow' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          {t('oauth.allow')}
        </Button>
        <Button
          variant="outline"
          className={`${AUTH_BUTTON_CLASS} sm:flex-1`}
          disabled={submitting !== null}
          onClick={() => void decide(false)}
        >
          {submitting === 'deny' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          {t('oauth.deny')}
        </Button>
      </div>

      {email ? <p className="text-center text-[13px] text-muted-foreground">{t('oauth.signedInAs', { email })}</p> : null}
    </div>
  )
}
